/** @jsx jsx */
import React from "react"
import { jsx } from "theme-ui"
import { Grid } from "@theme-ui/components"
import { graphql, useStaticQuery } from "gatsby"
import { navigate } from "@reach/router"
import SEO from "@components/seo"
import CTAHeadingBlock from "@components/CTAHeadingBlock"
import Layout from "@components/Layouts"
import Container from "@components/Container"
import CalloutTextBlocks from "@components/CalloutTextBlocks"
import ArticleCard from "@components/ArticleCard"
import TwoColumnSection, {
  Block as ColumnBlock,
  ImageBlock as ColumnImageBlock,
} from "@components/TwoColumnSection"
import Section from "@components/Section"
import ImageTextBlocksList from "@components/ImageTextBlocksList"
import Link from "@components/Link"
import { rem } from "@src/theme"
import PartnersSection from "@components/PartnersSection"
import { DonateSection } from "@components/ImpactFooter"
import PillList from "@components/PillList"
import { TAG_OPTIONS } from "@content/forms"
import pageContent from "@content/homepage"
import { formatMarkdownToHtml } from "@helpers"
import MarkdownBody from "@modules/MarkdownBody"
import { getSlug } from "@helpers/utils/getSlug"

export function Head () {
  return (
    <>
      <meta name="google-site-verification" content="FOfHKEpKWPDepPUlmTD7yyUlLI77iv4Mi3QOTPyq3ws" />
    </>
  )
}

const PageHome = () => {
  const data = useStaticQuery(
    graphql`
      query {
        articles: allMarkdownRemark(
          filter: {
            fileAbsolutePath: { glob: "**/article/*.md" }
            frontmatter: { tags: { in: ["homepage"] } }
          }
          sort: { frontmatter: { date: DESC } }
          limit: 4
        ) {
          edges {
            node {
              id
              frontmatter {
                title
                slug
                cardPreview
                tags
                image
              }
            }
          }
        }

        pageData: markdownRemark(
          fileAbsolutePath: { glob: "**/pages/home/index.md" }
        ) {
          frontmatter {
            header {
              ...Header
            }
            campaignMode
            partnersLogo
            banner
            ogimage
            ogdescription
          }
        }
      }
    `
  )

  const { frontmatter } = data.pageData
  const {
    header,
    campaignMode,
    partnersLogo,
    banner,
    ogimage,
    ogdescription,
  } = frontmatter

  return (
    <>
      <SEO title="Home" image={ogimage} ogdescription={ogdescription} />
      {banner && (
        <MarkdownBody
          html={formatMarkdownToHtml(banner)}
          sx={{
            bg: "turquoise",
            color: "darkPurple",
            textAlign: "center",
            py: rem(25),

            "h1,h2,h3,h4,h5": {
              my: 0,
            },
          }}
        />
      )}

      <TwoColumnSection
        col1={
          <div>
            <ColumnBlock
              styles={{
                bg: "purple",
                color: "pink",
              }}
            >
              <CTAHeadingBlock heading={header.heading} />
            </ColumnBlock>
            <ColumnBlock
              styles={{
                bg: "pink",
                color: "darkPurple",
              }}
            >
              <CTAHeadingBlock
                heading={header.subheading}
                body={header.subtext}
                buttonLabel={header.ctaButton.label}
                buttonLink={header.ctaButton.link}
                smallHeading
              />
            </ColumnBlock>
          </div>
        }
        col2={<ColumnImageBlock image={header.image} />}
      />
      <Container>
        <Section>
          <CalloutTextBlocks blocks={pageContent.calloutTextBlocks} />
          {campaignMode && (
            <div
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: [0, 0, rem(40)],
              }}
            >
              <Link to="/about">More about our work</Link>
            </div>
          )}
        </Section>
      </Container>

      <Section
        sx={{
          bg: "pink",
        }}
      >
        <PillList
          options={TAG_OPTIONS}
          heading="Brush up on your knowledge"
          onPillClick={tag => navigate(`/articles?tag=${tag}`)}
        />
        <Container>
          <Grid gap={rem(32)} columns={[1, 2, 3, 4]}>
            {data.articles.edges.map(item => (
              <ArticleCard
                key={item.node.id}
                data={{
                  ...item,
                  slug: getSlug(item),
                  preview: item.node.frontmatter.cardPreview,
                }}
              />
            ))}
          </Grid>
        </Container>
      </Section>

      <ImageTextBlocksList blocks={pageContent.imageTextBlocks} />
      <Section noPb>
        <PartnersSection img={partnersLogo} />
        <DonateSection />
      </Section>
    </>
  )
}

export default PageHome
